import { useMemo } from 'react';
import useAggregatedGetDevicesConnectionStatusCountBySubWorkSpaces from './useAggregatedGetDevicesConnectionStatusCountBySubworkspaces';

interface AggregatedDeviceStatus {
  mqttConnected: number;
  count: number;
  isFetching: boolean;
  isLoading: boolean;
  error?: any;
}

/**
 * Hook that aggregates device connection status from all subworkspaces
 *
 * @returns Aggregated device connection status across all workspaces
 *
 * @example
 * ```ts
 * const { mqttConnected, count, isFetching } = useAggregatedDevicesConnectionStatusCount();
 * ```
 */
function useAggregatedDevicesConnectionStatusCount(): AggregatedDeviceStatus {
  const aggregatedChildrenResponse =
    useAggregatedGetDevicesConnectionStatusCountBySubWorkSpaces();

  const currentData = aggregatedChildrenResponse.currentData;

  return useMemo(
    () => ({
      mqttConnected: currentData?.mqttConnected ?? 0,
      count: currentData?.count ?? 0,
      isFetching: aggregatedChildrenResponse.isFetching,
      isLoading:
        aggregatedChildrenResponse.isFetching && currentData === undefined,
      error: aggregatedChildrenResponse.error,
    }),
    [
      aggregatedChildrenResponse.error,
      aggregatedChildrenResponse.isFetching,
      currentData,
    ]
  );
}

export default useAggregatedDevicesConnectionStatusCount;
